// src/contexts/SubdomainBrandingContext.tsx
// Resolves the facility from the subdomain and provides branding to the app.
// *.stillwatersacademy.com → facility lookup by public_slug → branded login + shell.
// No subdomain (or 'www') → standard SWA branding.
//
// useOrgVocab() — the single source of truth for all context-sensitive labels.
// Every admin page, student dashboard, modal, and toast reads from here so an
// employee of a corporate client never sees "Student", a congregation member
// never reads "Class", and a business HR manager is a "Course Manager" not a "Teacher".

import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/integrations/supabase/client'

// ─── Org types ─────────────────────────────────────────────────────────────────
export type OrgType =
  | 'school'
  | 'university'
  | 'ngo'
  | 'church'
  | 'business'
  | 'sports_club'
  | 'community'
  | 'family_edu'
  | 'platform'

// ─── Vocabulary shape ──────────────────────────────────────────────────────────
export interface OrgVocab {
  staffPlural: string        // "Teachers" / "Trainers" / "Course Managers" …
  learnerPlural: string      // "Students" / "Employees" / "Members" …
  coursePlural: string       // "Subjects" / "Training Modules" / "Programs" …
  staffSingular: string      // "Teacher" / "Trainer" / "Course Manager" …
  learnerSingular: string    // "Student" / "Employee" / "Member" …
  courseSingular: string     // "Subject" / "Training Module" / "Program" …
  adminTitle: string         // "Principal" / "HR Manager" / "Coordinator" …
  hubLabel: string           // "School Hub" / "Corporate Learning Portal" …
  hubTagline: string
  learnerPortalName: string  // "Learning Hub" / "Training Portal" / "Study Centre" …
  inviteStaffTitle: string   // "Invite Teacher" / "Add Trainer" / "Add Course Manager" …
  inviteLearnerTitle: string // "Invite Student" / "Add Employee" / "Invite Member" …
  newCourseLabel: string     // "New Class" / "New Training Module" / "New Program" …
  learnerTabLabel: string    // "Students" / "Participants" / "Members" …
  staffInvitedMsg: string
  learnerInvitedMsg: string
  readOnlyLibraryNote: string
  subjectStatLabel: string   // "Subjects" / "Modules" / "Courses" …
}

// ─── Vocabulary table ──────────────────────────────────────────────────────────
export const ORG_VOCAB: Record<OrgType, OrgVocab> = {
  school: {
    staffPlural: 'Teachers', learnerPlural: 'Students', coursePlural: 'Subjects',
    staffSingular: 'Teacher', learnerSingular: 'Student', courseSingular: 'Subject',
    adminTitle: 'Principal', hubLabel: 'School Hub',
    hubTagline: 'theory delivered, evidence verified',
    learnerPortalName: 'Learning Hub',
    inviteStaffTitle: 'Invite Teacher', inviteLearnerTitle: 'Invite Student',
    newCourseLabel: 'New Subject', learnerTabLabel: 'Students',
    staffInvitedMsg: 'Teacher invited successfully', learnerInvitedMsg: 'Student invited',
    readOnlyLibraryNote: 'Ask your teacher to add this course to your classes.',
    subjectStatLabel: 'Subjects',
  },
  university: {
    staffPlural: 'Lecturers', learnerPlural: 'Students', coursePlural: 'Courses',
    staffSingular: 'Lecturer', learnerSingular: 'Student', courseSingular: 'Course',
    adminTitle: 'Dean / Coordinator', hubLabel: 'University Hub',
    hubTagline: 'theory delivered, credentials verified',
    learnerPortalName: 'Academic Portal',
    inviteStaffTitle: 'Invite Lecturer', inviteLearnerTitle: 'Enrol Student',
    newCourseLabel: 'New Course', learnerTabLabel: 'Students',
    staffInvitedMsg: 'Lecturer invited successfully', learnerInvitedMsg: 'Student enrolled',
    readOnlyLibraryNote: 'Ask your lecturer to add this course to your programme.',
    subjectStatLabel: 'Courses',
  },
  business: {
    staffPlural: 'Course Managers', learnerPlural: 'Employees', coursePlural: 'Training Modules',
    staffSingular: 'Course Manager', learnerSingular: 'Employee', courseSingular: 'Training Module',
    adminTitle: 'Manager', hubLabel: 'Corporate Learning Portal',
    hubTagline: 'staff development, verified',
    learnerPortalName: 'Training Portal',
    inviteStaffTitle: 'Add Course Manager', inviteLearnerTitle: 'Assign Employee',
    newCourseLabel: 'New Training Module', learnerTabLabel: 'Participants',
    staffInvitedMsg: 'Course Manager added successfully', learnerInvitedMsg: 'Employee assigned',
    readOnlyLibraryNote: 'Ask your Course Manager to add this module to your training path.',
    subjectStatLabel: 'Modules',
  },
  ngo: {
    staffPlural: 'Facilitators', learnerPlural: 'Participants', coursePlural: 'Programs',
    staffSingular: 'Facilitator', learnerSingular: 'Participant', courseSingular: 'Program',
    adminTitle: 'Programme Coordinator', hubLabel: 'NGO Learning Hub',
    hubTagline: 'community learning, evidence of impact',
    learnerPortalName: 'Learning Centre',
    inviteStaffTitle: 'Invite Facilitator', inviteLearnerTitle: 'Register Participant',
    newCourseLabel: 'New Program', learnerTabLabel: 'Participants',
    staffInvitedMsg: 'Facilitator invited successfully', learnerInvitedMsg: 'Participant registered',
    readOnlyLibraryNote: 'Ask your facilitator to add this program to your learning path.',
    subjectStatLabel: 'Programs',
  },
  church: {
    staffPlural: 'Pastors & Leaders', learnerPlural: 'Members', coursePlural: 'Studies',
    staffSingular: 'Pastor / Leader', learnerSingular: 'Member', courseSingular: 'Study',
    adminTitle: 'Ministry Coordinator', hubLabel: 'Church Learning Hub',
    hubTagline: 'discipleship with evidence',
    learnerPortalName: 'Ministry Portal',
    inviteStaffTitle: 'Invite Pastor / Leader', inviteLearnerTitle: 'Invite Member',
    newCourseLabel: 'New Study', learnerTabLabel: 'Members',
    staffInvitedMsg: 'Leader invited successfully', learnerInvitedMsg: 'Member invited',
    readOnlyLibraryNote: 'Ask your pastor or leader to add this study to your group.',
    subjectStatLabel: 'Studies',
  },
  sports_club: {
    staffPlural: 'Coaches', learnerPlural: 'Athletes', coursePlural: 'Programs',
    staffSingular: 'Coach', learnerSingular: 'Athlete', courseSingular: 'Program',
    adminTitle: 'Club Director', hubLabel: 'Sports Academy Hub',
    hubTagline: 'athlete education and certification',
    learnerPortalName: 'Athlete Portal',
    inviteStaffTitle: 'Invite Coach', inviteLearnerTitle: 'Register Athlete',
    newCourseLabel: 'New Program', learnerTabLabel: 'Athletes',
    staffInvitedMsg: 'Coach invited successfully', learnerInvitedMsg: 'Athlete registered',
    readOnlyLibraryNote: 'Ask your coach to add this program to your training.',
    subjectStatLabel: 'Programs',
  },
  community: {
    staffPlural: 'Mentors', learnerPlural: 'Learners', coursePlural: 'Courses',
    staffSingular: 'Mentor', learnerSingular: 'Learner', courseSingular: 'Course',
    adminTitle: 'Hub Coordinator', hubLabel: 'Community Learning Hub',
    hubTagline: 'lifelong learning, evidence of growth',
    learnerPortalName: 'Learning Hub',
    inviteStaffTitle: 'Invite Mentor', inviteLearnerTitle: 'Invite Learner',
    newCourseLabel: 'New Course', learnerTabLabel: 'Learners',
    staffInvitedMsg: 'Mentor invited successfully', learnerInvitedMsg: 'Learner invited',
    readOnlyLibraryNote: 'Ask your mentor to add this course to your learning plan.',
    subjectStatLabel: 'Courses',
  },
  family_edu: {
    staffPlural: 'Tutors', learnerPlural: 'Students', coursePlural: 'Courses',
    staffSingular: 'Tutor', learnerSingular: 'Student', courseSingular: 'Course',
    adminTitle: 'Co-op Admin', hubLabel: 'Learning Centre Hub',
    hubTagline: 'personalised learning, verified results',
    learnerPortalName: 'Learning Centre',
    inviteStaffTitle: 'Invite Tutor', inviteLearnerTitle: 'Invite Student',
    newCourseLabel: 'New Course', learnerTabLabel: 'Students',
    staffInvitedMsg: 'Tutor invited successfully', learnerInvitedMsg: 'Student invited',
    readOnlyLibraryNote: 'Ask your tutor to add this course to your studies.',
    subjectStatLabel: 'Courses',
  },
  platform: {
    staffPlural: 'Course Managers', learnerPlural: 'Learners', coursePlural: 'Courses',
    staffSingular: 'Course Manager', learnerSingular: 'Learner', courseSingular: 'Course',
    adminTitle: 'Platform Admin', hubLabel: 'Learning Platform',
    hubTagline: 'branded learning, verified at scale',
    learnerPortalName: 'Learning Portal',
    inviteStaffTitle: 'Add Course Manager', inviteLearnerTitle: 'Invite Learner',
    newCourseLabel: 'New Course', learnerTabLabel: 'Learners',
    staffInvitedMsg: 'Course Manager added successfully', learnerInvitedMsg: 'Learner invited',
    readOnlyLibraryNote: 'Ask your course manager to add this course to your learning path.',
    subjectStatLabel: 'Courses',
  },
}

// Default — standard SWA / homeschool context (no org type set)
export const DEFAULT_ORG_VOCAB: OrgVocab = {
  staffPlural: 'Teachers', learnerPlural: 'Students', coursePlural: 'Subjects',
  staffSingular: 'Teacher', learnerSingular: 'Student', courseSingular: 'Subject',
  adminTitle: 'Principal', hubLabel: 'Education Hub',
  hubTagline: 'theory delivered, evidence verified',
  learnerPortalName: 'Learning Hub',
  inviteStaffTitle: 'Invite Teacher', inviteLearnerTitle: 'Invite Student',
  newCourseLabel: 'New Class', learnerTabLabel: 'Students',
  staffInvitedMsg: 'Teacher invited successfully', learnerInvitedMsg: 'Student invited',
  readOnlyLibraryNote: 'Ask your parent or teacher to add this course to your classes.',
  subjectStatLabel: 'Subjects',
}

// ─── Branding interface ────────────────────────────────────────────────────────
export interface FacilityBranding {
  facility_id: string
  display_name: string
  logo_url: string | null
  primary_color: string
  secondary_color: string
  org_type: OrgType | null
  is_branded: boolean   // false = standard SWA, true = white-label
}

export const SubdomainBrandingContext = createContext<FacilityBranding>({
  facility_id: '',
  display_name: 'Still Waters Academy',
  logo_url: null,
  primary_color: '#06060c',
  secondary_color: '#d4a843',
  org_type: null,
  is_branded: false,
})

export function SubdomainBrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<FacilityBranding>({
    facility_id: '',
    display_name: 'Still Waters Academy',
    logo_url: null,
    primary_color: '#06060c',
    secondary_color: '#d4a843',
    org_type: null,
    is_branded: false,
  })

  useEffect(() => {
    const hostname = window.location.hostname
    // Extract subdomain:
    //   'oxford.stillwatersacademy.com' → 'oxford'  (3 parts, prod)
    //   'bigdogbuilding.localhost'       → 'bigdogbuilding'  (2 parts, dev)
    //   'localhost'                      → null  (1 part, main dev)
    const parts = hostname.split('.')
    const isLocalhostDev = parts.length === 2 && parts[1].startsWith('localhost')
    const isProdSubdomain = parts.length > 2 && parts[0] !== 'www'
    const subdomain = (isLocalhostDev || isProdSubdomain) && parts[0] !== 'www' ? parts[0] : null

    if (!subdomain) return  // standard SWA experience

    // Look up facility by public_slug matching the subdomain (via public_facility_branding view —
    // facilities table is dark to anon after RLS scoping)
    ;(supabase.from('public_facility_branding' as any) as any)
      .select('id, name, branding')
      .eq('public_slug', subdomain)
      .maybeSingle()
      .then(({ data }: { data: any }) => {
        if (data?.branding) {
          const b = data.branding as any
          setBranding({
            facility_id: data.id,
            display_name: b.display_name || data.name,
            logo_url: b.logo_url || null,
            primary_color: b.primary_color || '#06060c',
            secondary_color: b.secondary_color || '#d4a843',
            org_type: (b.org_type as OrgType) || null,
            is_branded: true,
          })
          // Set page title + favicon to their branding
          document.title = `${b.display_name || data.name} — Learning Platform`
        }
      })
  }, [])

  return (
    <SubdomainBrandingContext.Provider value={branding}>
      {children}
    </SubdomainBrandingContext.Provider>
  )
}

// ─── Hooks ─────────────────────────────────────────────────────────────────────

/** Raw branding object — colors, display_name, logo_url, org_type, is_branded */
export const useFacilityBranding = () => useContext(SubdomainBrandingContext)

/**
 * useOrgVocab(overrideType?)
 *
 * Returns the vocabulary set for the current org.
 * Without an argument, reads org_type from the nearest SubdomainBrandingContext.
 * Pass an explicit OrgType to override (useful in admin pages during org-type selection).
 *
 * @example
 *   const vocab = useOrgVocab()
 *   <button>{vocab.inviteStaffTitle}</button>  // "Add Course Manager" for business
 */
export function useOrgVocab(overrideType?: OrgType | null): OrgVocab {
  const branding = useContext(SubdomainBrandingContext)
  const type = overrideType !== undefined ? overrideType : branding.org_type
  if (!type) return DEFAULT_ORG_VOCAB
  return ORG_VOCAB[type] ?? DEFAULT_ORG_VOCAB
}

/**
 * getOrgVocab(orgType)
 * Pure function — for use outside React (e.g. utility helpers).
 */
export function getOrgVocab(type: OrgType | null | undefined): OrgVocab {
  if (!type) return DEFAULT_ORG_VOCAB
  return ORG_VOCAB[type] ?? DEFAULT_ORG_VOCAB
}